import { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { HashLink } from "react-router-hash-link";
import { ArrowRightCircle } from "react-bootstrap-icons";
import starTrackerImg from "../assets/img/StarTracker-Transparent.png";

const performanceSpecs = [
  ["Pitch / Yaw Accuracy", "30 arcseconds (3σ)"],
  ["Roll Accuracy", "200 arcseconds (3σ)"],
  ["Update Rate", "10 Hz"],
  ["Maximum Slew Rate — Tip / Tilt", "< 0.3°/s"],
  ["Maximum Slew Rate — Roll", "< 0.6°/s"],
];

const physicalSpecs = [
  ["Outer Dimensions", "29 × 29 × 38.1 mm"],
  ["Mass Without Baffle", "42 g"],
  ["Operating Temperature", "−40°C to +50°C"],
  ["Communication Interfaces", "TTL UART or RS-485"],
];

const SpecificationTable = ({ title, rows }) => {
  return (
    <div className="startracker-spec-table">
      <h3>{title}</h3>

      <dl>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const StarTracker = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="startracker-page">

      {/* HERO */}
      <section className="startracker-product-hero">
        <Container>
          <Row className="align-items-center g-5">

            <Col xs={12} lg={6}>
              <div className="startracker-product-hero__content">
                <span className="startracker-product-eyebrow">
                  SPACEBORNE ATTITUDE DETERMINATION
                </span>

                <h1>Compact Star Tracker</h1>

                <p>
                  Precise and autonomous attitude determination in a compact,
                  lightweight and low-power package developed for small
                  satellite missions.
                </p>

                <div className="startracker-product-highlights">
                  <div>
                    <strong>30 arcsec</strong>
                    <span>Pitch / Yaw Accuracy</span>
                  </div>

                  <div>
                    <strong>10 Hz</strong>
                    <span>Update Rate</span>
                  </div>

                  <div>
                    <strong>42 g</strong>
                    <span>Mass Without Baffle</span>
                  </div>
                </div>

                <HashLink
                  smooth
                  className="startracker-product-button"
                  to="/#connect"
                >
                  Contact Our Team
                  <ArrowRightCircle aria-hidden="true" />
                </HashLink>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="startracker-product-hero__image">
                <img
                  src={starTrackerImg}
                  alt="Kristal Fotonik Compact Star Tracker"
                />
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section className="startracker-product-overview">
        <Container>
          <Row className="g-5">

            <Col xs={12} lg={7}>
              <span className="startracker-section-eyebrow">
                PRODUCT OVERVIEW
              </span>

              <h2>
                Reliable orientation data for compact spacecraft
              </h2>

              <p>
                The Kristal Fotonik Compact Star Tracker observes the star
                field and autonomously determines spacecraft orientation
                without requiring external image processing.
              </p>

              <p>
                Its compact mechanical design, low mass and low-power
                architecture make it suitable for CubeSat, pico-satellite
                and nanosatellite platforms, as well as larger spacecraft
                with strict size, mass and power requirements.
              </p>

              <p>
                A plug-and-play architecture enables straightforward
                integration into spacecraft attitude determination and
                control systems.
              </p>
            </Col>

            <Col xs={12} lg={5}>
              <aside
                className="startracker-product-advantages"
                aria-label="Star Tracker product advantages"
              >
                <h3>Designed for integration</h3>

                <ul>
                  <li>Fully autonomous attitude determination</li>
                  <li>Compact CubeSat-compatible form factor</li>
                  <li>Low size, mass and power footprint</li>
                  <li>TTL UART or RS-485 communication</li>
                  <li>Mission-specific baffle configurations</li>
                </ul>
              </aside>
            </Col>

          </Row>
        </Container>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section
        className="startracker-product-specifications"
        aria-labelledby="startracker-specifications-title"
      >
        <Container>
          <div className="startracker-product-specifications__heading">
            <span className="startracker-section-eyebrow">
              TECHNICAL DATA
            </span>

            <h2 id="startracker-specifications-title">
              Key Specifications
            </h2>

            <p>
              Core performance, mechanical and environmental characteristics
              for spacecraft integration.
            </p>
          </div>

          <Row className="g-4">
            <Col xs={12} lg={6}>
              <SpecificationTable
                title="Performance"
                rows={performanceSpecs}
              />
            </Col>

            <Col xs={12} lg={6}>
              <SpecificationTable
                title="Mechanical & Environmental"
                rows={physicalSpecs}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* CONTACT CTA */}
      <section className="startracker-product-cta">
        <Container>
          <div>
            <span>MISSION-SPECIFIC INTEGRATION</span>
            <h2>Let’s discuss your mission requirements.</h2>

            <p>
              Contact our engineering team for integration details,
              configuration options and product availability.
            </p>
          </div>

          <HashLink
            smooth
            className="startracker-product-button startracker-product-button--light"
            to="/#connect"
          >
            Get In Touch
            <ArrowRightCircle aria-hidden="true" />
          </HashLink>
        </Container>
      </section>

    </main>
  );
};

export default StarTracker;